/**
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 *
 * @format
 */
'use strict';

import * as React from 'react';
import { useEffect } from 'react';
import View from '../../exports/View';
import UIManager from '../../exports/UIManager';
import Platform from '../../exports/Platform';
import TVEventHandler from '../../exports/TVEventHandler';
export default function useTVEvents(hostRef, config) {
  useEffect(
    function () {
      if (Platform.isTV && config.hasTVPreferredFocus) {
        if (hostRef.current) {
          UIManager.focus(hostRef.current);
        }
      }
      if (hostRef.current) {
        if (config.nextFocusUp === null) {
          hostRef.current.setAttribute('preventFocusUp', '');
        } else if (hostRef.current.hasAttribute('preventFocusUp')) {
          hostRef.current.removeAttribute('preventFocusUp');
        }
        if (config.nextFocusDown === null) {
          hostRef.current.setAttribute('preventFocusDown', '');
        } else if (hostRef.current.hasAttribute('preventFocusDown')) {
          hostRef.current.removeAttribute('preventFocusDown');
        }
        if (config.nextFocusLeft === null) {
          hostRef.current.setAttribute('preventFocusLeft', '');
        } else if (hostRef.current.hasAttribute('preventFocusLeft')) {
          hostRef.current.removeAttribute('preventFocusLeft');
        }
        if (config.nextFocusRight === null) {
          hostRef.current.setAttribute('preventFocusRight', '');
        } else if (hostRef.current.hasAttribute('preventFocusRight')) {
          hostRef.current.removeAttribute('preventFocusRight');
        }
      }
    },
    [config.hasTVPreferredFocus, hostRef]
  );

  function onKeyEvent(event) {
    var type = event.type,
      key = event.key; // Get tvEvent
    if (event.defaultPrevented || type !== 'keydown') {
      return;
    }

    let inModal = false;
    let current = hostRef.current;
    while(current && current.hasAttribute) {
      // Modals must have this prop set. Walk up the parent elements testing if we are in a modal
      if (current.hasAttribute(`aria-modal`) && current.getAttribute(`aria-modal`)) {
        inModal = true;
        break;
      }
      current = current.parentNode;
    }

    // Prevent key handling from bubbling to parents outside of the modal
    if (inModal) {
      event.preventDefault();
    }

    var tvEvent = TVEventHandler.getTVEvent(event); // Dispatch 'select' tvEvent to component

    if (tvEvent.eventType === 'select') {
      if (config.onPress) {
        config.onPress(tvEvent);
      }
    } // Dispatch tvEvent to all listeners

    TVEventHandler.dispatchEvent(tvEvent); // Handle next focus

    var nextElement = undefined; // Check nextFocus properties set using : nextFocus*={findNodeHandle(ref.current)}

    if (key === 'ArrowUp') {
      nextElement = config.nextFocusUp;
    } else if (key === 'ArrowRight') {
      nextElement = config.nextFocusRight;
    } else if (key === 'ArrowDown') {
      nextElement = config.nextFocusDown;
    } else if (key === 'ArrowLeft') {
      nextElement = config.nextFocusLeft;
    }

    if (nextElement) {
      // Focus if element is focusable
      UIManager.focus(nextElement);
      // Stop event propagation
      event.stopPropagation();
    } // Check nextFocus properties set using : ref.current.setNativeProps({nextFocus*: nativeID}

    var nextFocusID = ''; // Check nextFocus* properties

    if (hostRef.current.hasAttribute('nextFocusUp') && key === 'ArrowUp') {
      nextFocusID = hostRef.current.getAttribute('nextFocusUp');
    } else if (hostRef.current.hasAttribute('nextFocusRight') && key === 'ArrowRight') {
      nextFocusID = hostRef.current.getAttribute('nextFocusRight');
    } else if (hostRef.current.hasAttribute('nextFocusDown') && key === 'ArrowDown') {
      nextFocusID = hostRef.current.getAttribute('nextFocusDown');
    } else if (hostRef.current.hasAttribute('nextFocusLeft') && key === 'ArrowLeft') {
      nextFocusID = hostRef.current.getAttribute('nextFocusLeft');
    } else if (hostRef.current.hasAttribute('nextFocusForward') && key === 'ArrowRight') {
      nextFocusID = hostRef.current.getAttribute('nextFocusForward');
    }

    if (nextFocusID && nextFocusID !== '') {
      // Get DOM element
      nextElement = document.getElementById(nextFocusID);

      if (nextElement) {
        // Focus is element if focusable
        UIManager.focus(nextElement); // Stop event propagation

        event.stopPropagation();
      }
    }

    // Trigger Hardware Back Press for Back/Escape event keys
    // 10_009 is for samsung back button
    // 8 is for x1 back button
    // 27 for exit on vizio
    if(event.keyCode === 27 && window.VIZIO) {
      window.VIZIO.exitApplication();
      return;
    }

    // allow backspace key when in text input
    if(event.target.tagName === 'INPUT' && (event.key === 'Back' || event.keyCode === 8))
      return;

    if (type === 'keydown' && (key === 'Back' || key === 'Escape' || event.keyCode === 10009 || event.keyCode === 8)) {
      // eslint-disable-next-line no-undef
      var hwKeyEvent = new CustomEvent('hardwareBackPress', {});

      // If triggered from a modal element, do NOT fire a backpress. The modal handles this itself.
      // Only fire event if we are NOT in a modal. Otherwise, let the modal key handler pick this event up
      if(!inModal) {
        document.dispatchEvent(hwKeyEvent);
        event.stopPropagation();
      }
    }
  }

  var tvEventHandlers = Platform.isTV
    ? {
        onFocus: function onFocus(event) {
          // Get tvEvent
          var tvEvent = TVEventHandler.getTVEvent(event); // Dispatch tvEvent to component

          if (config.onFocus) {
            config.onFocus(tvEvent);
          } // Dispatch tvEvent to all listeners

          TVEventHandler.dispatchEvent(tvEvent);
        },
        onBlur: function onBlur(event) {
          // Get tvEvent
          var tvEvent = TVEventHandler.getTVEvent(event); // Dispatch tvEvent to component

          if (config.onBlur) {
            config.onBlur(tvEvent);
          } // Dispatch tvEvent to all listeners

          TVEventHandler.dispatchEvent(tvEvent);
        },
        onKeyDown: onKeyEvent,
        onKeyUp: onKeyEvent
      }
    : {};
  return tvEventHandlers;
}
